<template>
  <component :is="'style'" v-if="generateClassStyle">
    {{ generateClassStyle }}
  </component>
  <div
    class="question__answers"
    :class="{
      'animated shake': isError,
      'question__answers--images': question.answerType === QuizAnswerTypes.IMAGE,
      'question__answers--slider': question.answerType === QuizAnswerTypes.SLIDER,
      'question__answers--swipe': question.answerType === QuizAnswerTypes.SWIPE,
      ...generateColumnClasses
    }"
    :role="role"
    aria-labelledby="question"
  >
    <component
      :is="answerComponent"
      :question="question"
      :selected-answers="selectedAnswers"
      :show-other-peoples-answers="showOtherPeoplesAnswers"
      :show-correct-answer="showCorrectAnswer"
      :general-settings="generalSettings"
      :is-focusable="isFocusable"
      @selectAnswer="onSelectAnswer"
      @submitAnswer="$emit('submitAnswer', $event)"
    />
  </div>
</template>

<script lang="ts">
import type { Component, PropType } from 'vue';
import { computed, defineAsyncComponent, defineComponent, nextTick } from 'vue';
import type {
  GameQuizAnswerState,
  GameQuizGeneralState,
  GameQuizQuestionState
} from '@/src/components/games/quiz/Model';
import { QuizAnswerTypes } from '@/src/components/games/quiz/Model';
import { ImageEffectTypes } from '@/src/models/settings/AnswerLayoutSettingsModel';
import useDevice from '@/src/hooks/useDevice';
import AnswerTextItemsItem from '@/src/components/games/quiz/AnswerTextItemsItem.vue';

export default defineComponent({
  name: 'QuizAnswersItems',
  components: { AnswerTextItemsItem },
  props: {
    question: {
      type: Object as PropType<GameQuizQuestionState>,
      required: true
    },
    generalSettings: {
      type: Object as PropType<GameQuizGeneralState>,
      required: true
    },
    isError: {
      type: Boolean,
      default: false
    },
    selectedAnswers: {
      type: Object as PropType<GameQuizAnswerState[]>
    },
    showOtherPeoplesAnswers: {
      type: Boolean
    },
    showCorrectAnswer: {
      type: Boolean,
      required: true
    },
    isFocusable: {
      type: Boolean,
      default: false
    }
  },
  emits: ['selectAnswer', 'submitAnswer'],
  setup(props, context) {
    const onSelectAnswer = (answer: GameQuizAnswerState) => {
      context.emit('selectAnswer', answer);
    };

    const availableComponents: { [key: string]: () => Promise<Component> } = {
      text: () => import('@/src/components/games/quiz/AnswerTextItems.vue'),
      slider: () => import('@/src/components/games/quiz/AnswerSliderItem.vue'),
      image: () => import('@/src/components/games/quiz/AnswerImageItems.vue'),
      swipe: () => import('@/src/components/games/quiz/AnswerSwipeItem.vue')
    };

    let readyPromiseResolve: (() => void) | undefined;

    const preloadPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    const componentType = computed(() => {
      switch (props.question.answerType) {
        case QuizAnswerTypes.IMAGE:
          return 'image';
        case QuizAnswerTypes.SLIDER:
          return 'slider';
        case QuizAnswerTypes.SWIPE:
          return 'swipe';
        default:
          return 'text';
      }
    });

    const role = computed(() => {
      if (componentType.value === 'slider' || componentType.value === 'swipe') {
        return 'group';
      }
      return props.question.enableMultipleAnswers ? 'group' : 'radiogroup';
    });

    const answerComponent = defineAsyncComponent({
      loader: async () => {
        const component = await availableComponents[`${componentType.value}`]();
        await nextTick();
        if (readyPromiseResolve) {
          readyPromiseResolve();
        }
        return component;
      }
    });

    const generateColumnClasses = computed(() => {
      if (useDevice().isMobile) {
        return {
          'question__answers--images-1': props.question.answerLayout.state.imageColumns === 1,
          'question__answers--images-2': props.question.answerLayout.state.imageColumns === 2,
          'question__answers--images-3': props.question.answerLayout.state.imageColumns === 3
        };
      }

      return {
        'question__answers--images-desktop-1': props.question.answerLayout.state.imageColumns === 1,
        'question__answers--images-desktop-2': props.question.answerLayout.state.imageColumns === 2,
        'question__answers--images-desktop-3': props.question.answerLayout.state.imageColumns === 3
      };
    });

    const generateClassStyle = computed(() => {
      if (props.question.answerType === QuizAnswerTypes.SWIPE) {
        return '';
      }
      let style = '';

      const elementActiveStyle: string[] = [];
      const elementFocusStyle: string[] = [];

      if (props.question.answerLayout.state.slider) {
        const CSSSliderPrefix = ` .quiz .question .question__answers--slider `;
        if (props.question.answerLayout.state.slider.dotCheckedColor) {
          style += `${CSSSliderPrefix} .slider-marker {
            background-color: ${props.question.answerLayout.state.slider.dotCheckedColor} !important;
          }`;
        }
        if (props.question.answerLayout.state.slider.dotColor) {
          style += `${CSSSliderPrefix} .slider-handle {
            background-color: ${props.question.answerLayout.state.slider.dotColor} !important;
          }`;
        }
        if (props.question.answerLayout.state.slider.lineColor) {
          style += `${CSSSliderPrefix} .slider-connects {
            background-color: ${props.question.answerLayout.state.slider.lineColor} !important;
          }`;
        }

        if (props.question.answerLayout.state.slider.dotSize) {
          style += `${CSSSliderPrefix} .slider-handle {
            width: ${props.question.answerLayout.state.slider.dotSize} !important;
            height: ${props.question.answerLayout.state.slider.dotSize} !important;
            top: calc(-${props.question.answerLayout.state.slider.dotSize} / 2) !important;
            right: calc(-${props.question.answerLayout.state.slider.dotSize} / 2) !important;
          }`;
        }
      }

      if (props.question.answerLayout.state.focusAnswer?.backgroundColor) {
        elementFocusStyle.push(
          `background-color: ${props.question.answerLayout.state.focusAnswer.backgroundColor} !important;`
        );
      }

      if (props.question.answerLayout.state.focusAnswer?.textColor) {
        elementFocusStyle.push(`color: ${props.question.answerLayout.state.focusAnswer.textColor} !important;`);
      }

      if (elementFocusStyle.length > 0) {
        style += ` .quiz .question .question__answers:not(.question__answers--images):not(.question__answers--swipe) .has-focus-state:focus,
                   .category-desktop .quiz .question .question__answers:not(.question__answers--images):not(.question__answers--swipe) .has-focus-state:focus {
                     ${elementFocusStyle.join(' ')}
                   }`;
      }

      if (props.question.answerLayout.state.activeAnswer?.backgroundColor) {
        elementActiveStyle.push(
          `background-color: ${props.question.answerLayout.state.activeAnswer.backgroundColor} !important;`
        );
      }

      if (props.question.answerLayout.state.elementStyling?.answer?.normal) {
        style += `.quiz .question .question__answers:not(.question__answers--images):not(.question__answers--swipe) .question__answer-item {
          ${props.question.answerLayout.state.elementStyling.answer.normal}
           ${!props.generalSettings.showAnswerButton && !props.question.sliderAnswerButton?.enabled ? 'opacity:1' : ''}
        }`;
      }

      if (props.question.answerLayout.state.activeAnswer?.textColor) {
        elementActiveStyle.push(`color: ${props.question.answerLayout.state.activeAnswer.textColor} !important;`);
      }

      // Text hover style
      if (elementActiveStyle.length > 0) {
        style += ` .site .quiz .question .question__answers:not(.question__answers--images):not(.question__answers--swipe) .question__answer-item.question__answer-item--selected,\
                .category-desktop .quiz .question .question__answers:not(.question__answers--images):not(.question__answers--swipe) .question__answer-item:hover { ${elementActiveStyle.join(
                  ''
                )}} `;
      }

      if (props.question.answerLayout.state.imageEffect) {
        style +=
          ' .quiz .question .question__answers.question__answers--images:not(.question__answers--swipe) .question__answer-item {opacity: 1;} ';
      }

      switch (props.question.answerLayout.state.imageEffect?.type) {
        case ImageEffectTypes.BORDER:
          if (
            props.question.answerLayout.state.imageEffect?.borderThickness &&
            props.question.answerLayout.state.imageEffect?.color
          ) {
            style += ` .quiz .question .question__answers.question__answers--images .question__answer-item {\
                      border:${props.question.answerLayout.state.imageEffect.borderThickness}px solid transparent !important;\
                    }\
                    .site--desktop .quiz .question .question__answers.question__answers--images .question__answer-item:hover,\
                      .quiz .question .question__answers.question__answers--images .question__answer-item.question__answer-item--selected {\
                        border: ${props.question.answerLayout.state.imageEffect.borderThickness}px solid ${props.question.answerLayout.state.imageEffect.color} !important;\
                    } `;
          }
          break;
        case ImageEffectTypes.GREYSCALE:
          style += ` .quiz .question .question__answers.question__answers--images .question__answer-item {
                    -webkit-filter: grayscale(100%) !important;
                    filter: grayscale(100%) !important;
                  }
                  .site--desktop .quiz .question .question__answers.question__answers--images .question__answer-item:hover,
                  .quiz .question .question__answers.question__answers--images .question__answer-item.question__answer-item--selected {
                      -webkit-filter: grayscale(0%) !important;
                      filter: grayscale(0%) !important;
                  }`;
          break;
        case ImageEffectTypes.ZOOM_OUT:
          style += ` .site--desktop .quiz .question .question__answers.question__answers--images .question__answer-item:hover,
                  .quiz .question .question__answers.question__answers--images .question__answer-item.question__answer-item--selected {
                    -webkit-transform: scale(1.1) !important;
                    transform: scale(1.1) !important;
                    position: relative !important;
                    z-index: 1 !important;
                  }`;
          break;
        case ImageEffectTypes.ZOOM_IN:
          style += ` .site--desktop .quiz .question .question__answers.question__answers--images .question__answer-item:hover,
                  .quiz .question .question__answers.question__answers--images .question__answer-item.question__answer-item--selected {
                    -webkit-transform: scale(0.9) !important;
                    transform: scale(0.9) !important;
                  }`;
          break;
        case ImageEffectTypes.OVERLAY:
          style += ` .site--desktop .quiz .question .question__answers.question__answers--images .question__answer-item:hover,
                    .quiz .question .question__answers.question__answers--images .question__answer-item.question__answer-item--selected {opacity: 0.5 !important;}
                    .site--desktop .quiz .question .question__answers.question__answers--images .question__image:hover,
                    .quiz .question .question__answers.question__answers--images .question__image--selected {background-color: ${props.question.answerLayout.state.imageEffect.color} !important;}`;
          break;
      }

      return style;
    });

    return {
      role,
      generateClassStyle,
      answerComponent,
      QuizAnswerTypes,
      onSelectAnswer,
      generateColumnClasses,
      onBeforeEnter: async () => {
        await preloadPromise;
      }
    };
  }
});
</script>
